import { useState, useEffect, useContext } from "react";
import { Form, Modal, ModalBody } from "reactstrap";
import {
	RoundedButton,
	ButtonColor,
	ButtonCorner
} from "../../components/Buttons/RoundedButton";
import { successToast, errorToast } from "../../constants/toast";
import AccountDropdownList from "../../components/Dropdown/AccountDropdownList";
import { useMoveSelectedMutation } from "../../utilities/mutations/useMoveSelectedMutation";
import AccountImage from "../../components/AccountImage/AccountImage";
import { StructuralAccountType } from "./StructuralAccountType";
import { LocationModalContext } from "../../utilities/Contexts/LocationModalContext";
import PreventEnterSubmitForm from "../../components/Forms/PreventEnterSubmitForm";
import Button from "../../components/UIButton/UIButton";
import { AccountIdType } from "../../constants/AccountIdType";

export default function BulkMoveModal({
	isOpen,
	cancel,
	checkedItems,
	hierarchyData,
	allowedDestinationAccountTypeIds = [StructuralAccountType.Tenant, StructuralAccountType.Suborg],
	makeToast,
    handleMove
	}) {
	const [items, setItems] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedAccount, setSelectedAccount] = useState(null); // State to hold the selected account
	const [selectableAccounts, setSelectableAccounts] = useState([]); // State to hold the selectable accounts

	const { expandParentNodeAfterAddingData } = useContext(LocationModalContext);

	const moveAsync = useMoveSelectedMutation();


	useEffect(() => {
		if (hierarchyData !== undefined) {
			const tempItems = [];
			for (var i = 0; i < checkedItems.length; i++) {
				var selectedAccount = hierarchyData[checkedItems[i]];

				tempItems.push({
					accountId: selectedAccount.accountId,
					name: selectedAccount.data.name,
					type: selectedAccount.data.structuralAccountTypeId,
					data: selectedAccount.data
				});
			}

			setItems(tempItems);

			//Build list of accounts that can be moved to
			const tempAccountsToMove = [];
			const hierarchyNodes  = Object.values(hierarchyData)

			// Filter out accounts that are to be moved or have a parent that is in the list as well. This is used for the move to dropdown.
			for(let node of hierarchyNodes){
				if (node.accountId !== undefined
					&& node.accountId !== AccountIdType.GhostAccountId //Excluding ghost tenant
					&& allowedDestinationAccountTypeIds.includes(node.data?.structuralAccountTypeId) === true
					&& tempItems.find(x => x.accountId === node.accountId || parseInt(node.accountId) === x.data.parentId) === undefined) 				{
					tempAccountsToMove.push(node.data);
				}
			};

			setSelectableAccounts(tempAccountsToMove);
		}

		setIsLoading(false);
	}, [checkedItems, hierarchyData]);


	async function handleSubmit(event){
		event.preventDefault();
		setIsLoading(true);
		if (selectedAccount === null) {
			makeToast("Please select an account", errorToast);
			setIsLoading(false);
			return;
		}

		//remove accounts where parent is in list
		const rootItems = items.filter(x => {
			return !items.includes(y => y.data.parentAccountId === x.data.accountId)
		});

		try{
			var result = await moveAsync.mutateAsync({destinationAccount: selectedAccount.data.accountId, accountsToMove: rootItems.map(x => x.data)});

			if (result.length !== 0) {
				handleMove(selectedAccount);
				expandParentNodeAfterAddingData(selectedAccount?.data?.accountId);
				makeToast("Items moved successfully", successToast);
			} else {
				makeToast(
					"Error moving the following account record(s): " + result.data,
					errorToast
				);
			}
		}
		catch(e){
			makeToast(e.message, errorToast);
			setIsLoading(false);
			throw e;
		}
		
	};

	function handleSelectedAccountChange(selectedAccount) {
		setSelectedAccount(selectedAccount);
	};


	return (
		<Modal size="lg" isOpen={isOpen}>
			<PreventEnterSubmitForm onSubmit={handleSubmit} className="text-center">
				<div className="delete-selected-hdr">Move Selected to New Parent</div>
				<ModalBody>
					<div className="delete-selection-container">
						{checkedItems &&
							items.map(function (item, i) {
								return (
									<div key={item.accountId} value={item.accountId}>
										<AccountImage structuralAccountTypeId={item.type} /> {item.name}
									</div>
								);
							})}
					</div>
					<hr/>
                    <div>
						<b>Select the new parent organization:</b>
						<div className="w-50 container justify-content-center">
                        <AccountDropdownList accounts={selectableAccounts} selectedAccount={selectedAccount} onChange={handleSelectedAccountChange} isDisabled={isLoading} />
						</div>
						<p>* All entities in the list above will be moved to the destination specified in drop-down</p>
                    </div>
					<hr/>
					<div>
                		<p className="warning-txt">WARNING:</p>
                        <p className="delete-warning-txt">This action may affect user's and device's access to menu items, printer profiles and other data.</p>
                	</div>
					<div className="gap-6 align-items-center text-center">
						<Button
							id="cancelButton"
							variant="info"
							size="medium"
							onClick={cancel}
							disabled={isLoading}
						>Cancel</Button>
						<Button
							id="moveButton"
							disabled={isLoading || selectedAccount === null}
							variant="submit"
							size="medium"
							onClick={handleSubmit}
						>Confirm</Button>
					</div>
				</ModalBody>
			</PreventEnterSubmitForm>
		</Modal>
	);
}
